import { Box, styled } from '@mui/material';
import { useMemo, useRef } from 'react';
import { getGCFComponentScale } from 'src/utils/gcf-helper';

import { GcfComponentSectionWrapperProps } from './GcfComponentSection.interface';

const Wrapper = styled(Box)<{ $hidden?: boolean; $overflow?: boolean }>`
  position: relative;
  width: fit-content;
  height: fit-content;
  overflow: ${({ $overflow = false }: { $overflow: boolean }) =>
    $overflow ? 'visible' : 'hidden'};
  display: ${({ $hidden }: { $hidden: boolean }) =>
    $hidden ? 'none' : 'block'};
`;

const ContentWrapper = styled(Box)<{ scale?: number; $dimPreview?: boolean }>(
  ({ scale = 1, $dimPreview = false }) => `
  zoom: ${scale};
  box-sizing: content-box;
  background-color: #CDD3DB;
  ${
    $dimPreview
      ? `
    background-color: #CDD3DB !important;
    & * {
      background-image: none !important;
    }
    img, image, picture {
      display: none !important;
    }
  `
      : ''
  }
  & * {
    box-sizing: unset;
  }
`,
);

export const GcfComponentSectionWrapper = (
  props: GcfComponentSectionWrapperProps,
) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const containerSize = getContentSize(
    props.containerSize?.size,
    containerRef.current,
  );

  const scale = useMemo(
    () =>
      props.scale ??
      getGCFComponentScale(
        props.width,
        props.height,
        !!props.fullWidth,
        containerSize.width,
        containerSize.height,
      ),
    [
      containerSize.width,
      containerSize.height,
      props.fullWidth,
      props.height,
      props.scale,
      props.width,
    ],
  );

  return (
    <Wrapper
      $hidden={props.hidden}
      $overflow={props.overflow}
      ref={containerRef}
      sx={props.containerStyle}>
      {!props.containerSize?.isResizing && (
        <ContentWrapper
          width={props.width}
          height={props.height}
          scale={scale}
          $dimPreview={props.dimPreview}>
          {props.children}
        </ContentWrapper>
      )}
    </Wrapper>
  );
};

const getContentSize = (
  containerSize?: DOMRect,
  element?: HTMLDivElement | null,
) => {
  const extraWidth = (element?.offsetWidth ?? 0) - (element?.clientWidth ?? 0);
  const extraHeight =
    (element?.offsetHeight ?? 0) - (element?.clientHeight ?? 0);

  return {
    ...containerSize,
    width: (containerSize?.width ?? 0) - extraWidth,
    height: (containerSize?.height ?? 0) - extraHeight,
  };
};
